import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Button,
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Divider
} from "@mui/material";


import {
  getLevelFromDistrict,
  filterBySchoolType,
  getUniqueCategoriesCanonical,
  exportSchoolsToCSV,
  applyResultToSchool,
  exportSchoolsToExcel,
  calculateDistances,
  isSchoolSpecial,
  getCategoryKey,
  orderCategories,
  // virtual categories
  SPECIAL_CATEGORY_PE_NIPIAGOGEIA,
  SPECIAL_CATEGORY_PE_DIMOTIKA,
  SPECIAL_CATEGORY_DE_GENERIC,
  // PE helper
  getSpecialCategoryLabel,
  prettyLabel,
} from "./utils";

import OriginAddressInput from "./components/OriginAddressInput";
import ContactForm from './components/ContactForm';
import SupportCallout from "./components/SupportCallout";
import SupportModal from './components/SupportModal';

import { gaEvent } from "./analytics";




const SCHOOLS_JSON_PATH = "/schools_with_coords.json";

// Αντί για backendUrl
const backendUrl = process.env.REACT_APP_BACKEND_URL;


function App() {
  // UseStates for handles
  const [selectedLevel, setSelectedLevel] = useState("");
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [schools, setSchools] = useState([]);
  const [schoolTypeFilter, setSchoolTypeFilter] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [originAddress, setOriginAddress] = useState("");
  const [addressErrorTrigger, setAddressErrorTrigger] = useState(false);


  const [selectedMode, setSelectedMode] = useState("driving");
  const [avoidTolls, setAvoidTolls] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortBy, setSortBy] = useState('estimatedSeconds'); // π.χ. 'name' ή 'distanceCar'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' ή 'desc'
  const [supportOpen, setSupportOpen] = useState(false);


  const distanceCache = useRef(new Map());

  useEffect(() => {
  const gaId = process.env.REACT_APP_GA_ID;
  if (!gaId) return;

  // 1) Φόρτωσε το remote script
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(s);

  // 2) Αρχικοποίηση
  window.dataLayer = window.dataLayer || [];
  function gtag(){ window.dataLayer.push(arguments); }
  window.gtag = window.gtag || gtag;

  window.gtag('js', new Date());
  window.gtag('config', gaId, { send_page_view: true });

  return () => { document.head.removeChild(s); };
}, []);




  const filteredDistricts = useMemo(() => {
    if (!selectedLevel) return districts;
    return districts.filter((d) => getLevelFromDistrict(d) === selectedLevel);
  }, [districts, selectedLevel]);

  const filteredByType = useMemo(
        () => filterBySchoolType(schools, schoolTypeFilter),
        [schools, schoolTypeFilter]
      );

// ✅ Φιλτράρισμα ανά βαθμίδα
  const filteredSchools = useMemo(() => {
    if (!selectedCategories.length) return [];

    const set = new Set(selectedCategories);
    const baseList = filterBySchoolType(schools, schoolTypeFilter);

    // --- Πρωτοβάθμια: ειδικά ανά υπο-κατηγορία (νήπια/δημοτικά) ---
    if ((selectedLevel || '').toUpperCase().includes('Π')) {
      return baseList.filter((s) => {
        const baseKey = getCategoryKey(s.category);
        if (isSchoolSpecial(s)) {
          const label = getSpecialCategoryLabel(s); // → "Ειδικά Νηπιαγωγεία" / "Ειδικά Δημοτικά"
          return label ? set.has(label) : false;
        }
        // κανονικά: σύγκριση με το label σε πληθυντικό
        return set.has(prettyLabel(baseKey));
      });
    }

  // --- Δευτεροβάθμια: όλες οι κατηγορίες + γενική «Ειδικά σχολεία» ---
  const wantSpecialUmbrella = set.has(SPECIAL_CATEGORY_DE_GENERIC);

  return baseList.filter((s) => {
    const baseKey = getCategoryKey(s.category);
    const baseLabel = prettyLabel(baseKey);

    if (isSchoolSpecial(s)) {
      // περνά αν έχει τσεκαριστεί η «ομπρέλα» ή η ρητή κατηγορία του (π.χ. ΕΝΙΑΙΟ ΕΙΔΙΚΟ ΕΠΑΓ...)
      return wantSpecialUmbrella || set.has(baseLabel);
    }
    // μη-ειδικά: απλή αντιστοίχιση
    return set.has(baseLabel);
  });
}, [schools, schoolTypeFilter, selectedLevel, selectedCategories]);


// ✅ Στήσιμο checkboxes ανά βαθμίδα
  const uniqueCategories = useMemo(() => {
    const base = filterBySchoolType(schools, schoolTypeFilter);

    // --- Πρωτοβάθμια: σπάμε τα ειδικά ---
    if ((selectedLevel || '').toUpperCase().includes('Π')) {
      const nonSpecial = base.filter(s => !isSchoolSpecial(s));
      const normalKeys = new Set(nonSpecial.map(s => getCategoryKey(s.category)).filter(Boolean));

      const hasSpecialNip = base.some(s => isSchoolSpecial(s) && getCategoryKey(s.category) === 'ΝΗΠΙΑΓΩΓΕΙΟ');
      const hasSpecialDim = base.some(s => isSchoolSpecial(s) && getCategoryKey(s.category) === 'ΔΗΜΟΤΙΚΟ');

      let cats = [...normalKeys];
      if (hasSpecialNip) cats.push(SPECIAL_CATEGORY_PE_NIPIAGOGEIA);
      if (hasSpecialDim) cats.push(SPECIAL_CATEGORY_PE_DIMOTIKA);

      // ταξινόμηση με βάση canonical keys/virtuals
      const ordered = orderCategories(cats, selectedLevel);

      // εμφάνιση: κανονικές -> σε πληθυντικό, virtual -> όπως είναι
      return ordered.map(c => (
        c === SPECIAL_CATEGORY_PE_NIPIAGOGEIA || c === SPECIAL_CATEGORY_PE_DIMOTIKA
          ? c
          : prettyLabel(c)
      ));
    }

    // --- Δευτεροβάθμια: ΟΛΕΣ οι κατηγορίες (όπως πριν) + γενικό "Ειδικά σχολεία" ---
    const normalKeys = getUniqueCategoriesCanonical(base); // περιλαμβάνει και ειδικά π.χ. ΕΝΕΕΓΥ-Λ/ΕΕΕΕΚ/ΕΝΙΑΙΟ ...
    const hasAnySpecial = base.some(isSchoolSpecial);

    const cats = hasAnySpecial ? [...normalKeys, SPECIAL_CATEGORY_DE_GENERIC] : normalKeys;
    const ordered = orderCategories(cats, selectedLevel);

    // εμφάνιση: κανονικές -> πληθυντικός, η "ομπρέλα" όπως είναι
    return ordered.map(c => (c === SPECIAL_CATEGORY_DE_GENERIC ? c : prettyLabel(c)));
  }, [schools, schoolTypeFilter, selectedLevel]);


    const handleDistrictChange = (e) => {
      const value = e.target.value;
      //console.log("🟢 Επιλέχθηκε περιφέρεια:", value);
      setSelectedDistrict(value);
    };

  //----------------------------------------------------------//
  // Bring all districts data from json file
  useEffect(() => {
    fetch(SCHOOLS_JSON_PATH)
      .then((res) => res.json())
      .then((data) => {
        const allDistricts = data.map((entry) => entry.district);
        setDistricts(allDistricts);
      })
      .catch((error) =>
        console.error("❌ Σφάλμα στο load των περιφερειών:", error)
      );
  }, []);
  //----------------------------------------------------------//

  //----------------------------------------------------------//
  // Bring schools data from json file for the selected district
  useEffect(() => {
    if (!selectedDistrict) return;

    fetch(SCHOOLS_JSON_PATH)
      .then((res) => res.json())
      .then((data) => {
        const districtData = data.find(
          (entry) => entry.district === selectedDistrict
        );
        if (districtData) {
          const enriched = districtData.schools.map((school) => ({
            ...school,
            distanceCar: null,
            durationCar: null,
            durationInTraffic: null,
            distanceTransit: null,
            durationTransit: null,
            distanceWalking: null,
            durationWalking: null,
          }));
          setSchools(enriched);

          setSelectedCategories([]);
        } else {
          setSchools([]);
          setSelectedCategories([]);
        }

      })
      .catch((err) => {
        console.error("❌ Σφάλμα στο load σχολείων:", err);
      });
  }, [selectedDistrict]);
  //----------------------------------------------------------//

  //----------------------------------------------------------//
  // Create CSV file from the selected schools 
  const handleExport = () => {
    const exportData =
      selectedRows.length === 0
        ? filteredSchools
        : filteredSchools.filter((_, index) =>
            selectedRows.includes((index + 1).toString())
          );

    exportSchoolsToExcel(exportData);
  };



  // Create the rows for the table

  const rows = useMemo(() => {
    // 1) βάση: τα φιλτραρισμένα σχολεία
    const base = filteredSchools.filter((s) => s && typeof s === "object");

    // 2) εμπλουτισμός: υπολογίζουμε estimatedSeconds/estimatedText ΠΡΙΝ το sort
    const enriched = base.map((s) => {
      const dCarVal = s?.durationCar?.value ?? -1;
      const dTraVal = s?.durationInTraffic?.value ?? -1;

      let estimatedSeconds = Infinity; // όσα δεν έχουν χρόνο πάνε στο τέλος
      let estimatedText = "-";

      if (dCarVal >= 0 || dTraVal >= 0) {
        const maxVal = Math.max(dCarVal, dTraVal);
        estimatedSeconds = maxVal;

        // Πάρε το text του μεγαλύτερου, με fallback
        if (dTraVal === maxVal && s?.durationInTraffic?.text) {
          estimatedText = s.durationInTraffic.text;
        } else if (dCarVal === maxVal && s?.durationCar?.text) {
          estimatedText = s.durationCar.text;
        } else {
          estimatedText = s?.durationInTraffic?.text || s?.durationCar?.text || "-";
        }
      }

      return { ...s, estimatedSeconds, estimatedText };
    });

    // 3) sort
    let sorted = [...enriched];
    if (sortBy) {
      sorted.sort((a, b) => {
        let aVal, bVal;

        if (sortBy === "distanceCar") {
          aVal = a.distanceCar?.value ?? Infinity;
          bVal = b.distanceCar?.value ?? Infinity;
        } else if (sortBy === "estimatedSeconds") {
          aVal = a.estimatedSeconds ?? Infinity;
          bVal = b.estimatedSeconds ?? Infinity;
        } else {
          aVal = (a[sortBy] || "").toString().toLowerCase();
          bVal = (b[sortBy] || "").toString().toLowerCase();
        }

        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    // 4) τελικές γραμμές για τον πίνακα
    return sorted.map((s, index) => ({
      id: (index + 1).toString(),
      name: s.name || "-",
      address: s.address || "-",
      distanceCar: s.distanceCar,
      // κρατάμε τα raw αν τα χρειαστείς αλλού
      durationCar: s.durationCar,
      durationInTraffic: s.durationInTraffic,
      estimatedSeconds: s.estimatedSeconds,
      estimatedText: s.estimatedText,
    }));
  }, [filteredSchools, sortBy, sortDirection]);


    const handleSort = (column) => {
      if (sortBy === column) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(column);
        setSortDirection('asc');
      }
    };



  // Handler for the Delete Selected Schools Btn
  const handleDeleteSelected = () => {
    const schoolsToKeep = schools.filter((school) => {
      const isMatch = filteredSchools.findIndex(
        (f, index) =>
          f.name === school.name &&
          f.address === school.address &&
          !selectedRows.includes((index + 1).toString())
      );
      return isMatch !== -1;
    });

    setSchools(schoolsToKeep);
    setSelectedRows([]);
  };


        
    // console.log("➡️ rows:", rows);
    // console.log("➡️ selectedRows:", selectedRows);



  // Handler for the Calculate Distances Btn      
  const handleCalculateDistances = async () => {

    if (!originAddress) {
      setAddressErrorTrigger(true);

      // Reset μετά από λίγο για να δουλεύει το trigger ξανά
      setTimeout(() => setAddressErrorTrigger(false), 100);
      return;
    }
    //console.log(originAddress)

    
    
    setIsLoading(true);
    setProgress(0);
    
    const updated = await calculateDistances(
      originAddress,
      filteredSchools,
      selectedMode,
      avoidTolls,
      setProgress
    );
    setSchools(updated);
    

    setIsLoading(false);
    return updated; // ✅ ώστε να το πάρουμε στο onClick και να μετρήσουμε "results"
    
  };


  return (

      <div
      style={{
        backgroundImage: "url('/background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh"
      }}
    >
    <Container maxWidth="md">

      <Box display="flex" justifyContent="center" alignItems="center" mb={3}>
        <img 
          src="/logo.png" 
          alt="Teacher on the Road" 
          style={{ width: "450px", height: "auto" }} 
        />
      </Box>


      <Paper sx={{ p: 2, mb: 3 }} elevation={3}>
      <Typography variant="subtitle1" sx={{ mt: 4 }}>
        Επιλογή Βαθμίδας & Περιφέρειας
      </Typography>

      <FormControl fullWidth margin="normal">
        <InputLabel>Επιλογή Βαθμίδας</InputLabel>
        <Select
          value={selectedLevel}
          onChange={(e) => setSelectedLevel(e.target.value)}
          label="Επιλογή Βαθμίδας"
        >
          <MenuItem value="">-- Επιλογή --</MenuItem>
          <MenuItem value="Π.Ε.">Πρωτοβάθμια</MenuItem>
          <MenuItem value="Δ.Ε.">Δευτεροβάθμια</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal" disabled={!selectedLevel}>
        <InputLabel>Επιλογή Περιφέρειας</InputLabel>
        <Select
          value={selectedDistrict}
          onChange={handleDistrictChange}
          label="Επιλογή Περιφέρειας"
        >
          <MenuItem value="">-- Επιλογή --</MenuItem>
          {filteredDistricts.map((d, i) => (
            <MenuItem key={i} value={d}>
              {d}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <InputLabel>Τύπος Σχολείου</InputLabel>
        <Select
          value={schoolTypeFilter}
          onChange={(e) => setSchoolTypeFilter(e.target.value)}
          label="Τύπος Σχολείου"
        >
          <MenuItem value="">Όλα</MenuItem>
          <MenuItem value="Δημόσια">Δημόσια</MenuItem>
          <MenuItem value="Ιδιωτικά">Ιδιωτικά</MenuItem>
        </Select>
      </FormControl>

      <FormGroup row sx={{ mt: 3 }}>
        {uniqueCategories.map((cat, i) => (
          <FormControlLabel
            key={i}
            control={
              <Checkbox
                checked={selectedCategories.includes(cat)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedCategories([...selectedCategories, cat]);
                  } else {
                    setSelectedCategories(
                      selectedCategories.filter((c) => c !== cat)
                    );
                  }
                }}
              />
            }
            label={cat}
          />
        ))}
      </FormGroup>

      </Paper>
      
      <Paper sx={{ p: 2, mb: 3 }} elevation={3}>
      <Typography variant="subtitle1" sx={{ mt: 4 }}>
        Διεύθυνση Αφετηρίας
      </Typography>

      <OriginAddressInput
          onValidAddress={setOriginAddress}
          errorTrigger={addressErrorTrigger}
      />
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }} elevation={3}>
      <Typography variant="subtitle1" sx={{ mt: 4 }}>
        Επιλογές Μετακίνησης
      </Typography>

      <FormControl fullWidth margin="normal">
        <InputLabel>Μέσο Μετακίνησης</InputLabel>
        <Select
          value={selectedMode}
          onChange={(e) => setSelectedMode(e.target.value)}
          label="Μέσο Μετακίνησης"
        >
          <MenuItem value="driving">Οδήγηση</MenuItem>
          <MenuItem value="transit">ΜΜΜ</MenuItem>
        </Select>
      </FormControl>

      {selectedMode === "driving" && (
        <FormControlLabel
          control={
            <Checkbox
              checked={avoidTolls}
              onChange={(e) => setAvoidTolls(e.target.checked)}
            />
          }
          label="Αποφυγή Διοδίων"
        />
      )}
      
      </Paper>
     
      {filteredSchools.length > 0 && (

         <><Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Box display="grid" gap={2}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ textTransform: 'none' }}
              onClick={async () => {
                gaEvent('calculate_distances_click', {
                  mode: selectedMode,
                  avoid_tolls: selectedMode === 'driving' ? avoidTolls : undefined
                });
                const before = Date.now();
                const updated = await handleCalculateDistances();
                const after = Date.now();
                gaEvent('calculate_distances_done', {
                  mode: selectedMode,
                  avoid_tolls: selectedMode === 'driving' ? avoidTolls : undefined,
                  ms: after - before,
                  results: updated?.length ?? 0
                });
              }}
              disabled={isLoading}
            >
              Υπολόγισε αποστάσεις
            </Button>


            {isLoading && (
              <Box display="flex" justifyContent="center">
                <CircularProgress />
                <Box ml={2}>
                  <Typography>Υπολογίζονται αποστάσεις... ({progress}%)</Typography>
                </Box>
              </Box>
            )}

            <Button
              variant="outlined"
              fullWidth
              sx={{ textTransform: 'none' }}
              onClick={() => {
                gaEvent('export_excel', {
                  selected_rows: selectedRows.length,
                  visible_results: filteredSchools.length
                });
                handleExport();
              }}
              disabled={filteredSchools.length === 0}
            >
              Εξαγωγή σε Excel
            </Button>

          </Box>
          </Paper>
          
          <Box mt={4}>
              <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>
                {rows.length} Αποτελέσματα Σχολείων:
              </Typography>

              {selectedRows.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" gutterBottom sx={{ color: '#fff' }}>
                    Επιλεγμένα: {selectedRows.length}
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{ textTransform: 'none' }}
                    color="error"
                    onClick={handleDeleteSelected}
                  >
                    Διαγραφή Επιλεγμένων
                  </Button>
                </Box>
              )}


              <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.length === rows.length && rows.length > 0}
                          onChange={(e) => {
                            setSelectedRows(e.target.checked ? rows.map((r) => r.id) : []);
                          } } />
                      </TableCell>
                      <TableCell sx={{ width: 25 }}>Α/Α</TableCell>

                      <TableCell sortDirection={sortBy === 'name' ? sortDirection : false}>
                        <TableSortLabel
                          active={sortBy === 'name'}
                          direction={sortBy === 'name' ? sortDirection : 'asc'}
                          onClick={() => handleSort('name')}
                        >
                          Όνομα Σχολείου
                        </TableSortLabel>
                      </TableCell>

                      <TableCell sortDirection={sortBy === 'address' ? sortDirection : false}>
                        <TableSortLabel
                          active={sortBy === 'address'}
                          direction={sortBy === 'address' ? sortDirection : 'asc'}
                          onClick={() => handleSort('address')}
                        >
                          Διεύθυνση
                        </TableSortLabel>
                      </TableCell>


                      <TableCell sortDirection={sortBy === 'distanceCar' ? sortDirection : false}>
                        <TableSortLabel
                          active={sortBy === 'distanceCar'}
                          direction={sortBy === 'distanceCar' ? sortDirection : 'asc'}
                          onClick={() => handleSort('distanceCar')}
                        >
                          {selectedMode === 'transit' ? 'Απόσταση (ΜΜΜ)' : 'Απόσταση (Οδήγηση)'}
                        </TableSortLabel>
                      </TableCell>


                      <TableCell sx={{ width: 140 }} sortDirection={sortBy === 'estimatedSeconds' ? sortDirection : false}>
                        <TableSortLabel
                          active={sortBy === 'estimatedSeconds'}
                          direction={sortBy === 'estimatedSeconds' ? sortDirection : 'asc'}
                          onClick={() => handleSort('estimatedSeconds')}
                        >
                          Εκτιμώμενος Χρόνος
                        </TableSortLabel>
                      </TableCell>

                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRows.includes(row.id)}
                            onChange={() => {
                              setSelectedRows((prev) => prev.includes(row.id)
                                ? prev.filter((id) => id !== row.id)
                                : [...prev, row.id]
                              );
                            } } />
                        </TableCell>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.address}</TableCell>
                        <TableCell>{row.distanceCar?.text || "-"}</TableCell>

                        <TableCell>{row.estimatedText}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>





            </Box></>
            )}

            <Box mt={4}>
              <SupportCallout onOpen={() => setSupportOpen(true)} />
            </Box>

            <Box mt={2}>
              <ContactForm />
            </Box>

            <SupportModal
              irisPhone="6974201118"     // βάλε το δικό σου IRIS
              open={supportOpen}
              onClose={() => setSupportOpen(false)}
            />


            <br/>
                
      
    </Container>
    </div>
  );
}

export default App;
